/** 
 * Name : DataStore 
 * Description : Data structuring for the application 
 * Stores recurring resources... 
 * Such as images/profile data etc.  
 */

import LZString from "lz-string";
import logger from "../../helpers/logger";
import AuthService from "../auth-service";

interface Cache {
  getItem: (key: string) => unknown;
  setItem: (key: string, value) => void;
  removeItem: (key: string) => void;
}

interface DataStoreConfig {
  enableCaching?: boolean;
  enableCacheCompression?: boolean;
  cache?: Cache;
}

export default function DataStore(name: string, config?: DataStoreConfig) {
  config = config || { 
    // Caching is enabled by default
    // Set `config.enableCache = false` 
    // if you want to disable caching
    enableCaching: true, 

    // Cache data compression is enabled by default
    // Set `config.enableCacheCompression = false` 
    // if you want to disable cache compression
    enableCacheCompression: true,

    // Caching is done in the localStorage, 
    // overide this function and the set function to change this
    cache: localStorage, 
  };

  if(typeof name !== "string" || name.trim().length === 0) {
    throw new Error("DataStore object expects a name string property");
  }

  if(typeof config.enableCaching === "undefined") {
    config.enableCaching = true;
  }

  if(typeof config.enableCacheCompression === "undefined") {
    config.enableCacheCompression = true;
  }

  if(config.enableCaching) { 
    const isValidCache = (
      config.cache && 
      typeof config.cache === "object" && 
      typeof config.cache.getItem === "function" && 
      typeof config.cache.setItem === "function" && 
      typeof config.cache.removeItem === "function"
    );

    if(!isValidCache) { 
      config.cache = localStorage;
    }
  }

  this.queue = [];
  this.store = {}; // Internal store/cache, different from supplied cache.
  this.name = name.trim();
  this.config = config;
  this.cache = config.cache;

  if(this.config.enableCaching) {
    const store = deserialize(
      this.cache.getItem(this.name), 
      this.config.enableCacheCompression
    );

    if(store) {
      this.store = store;
    }

    for(const data in store) {
      //if(Object.prototype.hasOwnProperty.call(store, data)) {
      if(Object.hasOwn(store, data)) {
        this.queue.push(data);
      }
    }
  }
}

DataStore.prototype._getStore = function() {
  let store: object | undefined = this.store; 

  if(!store && this.config.enableCaching) {
    store = deserialize(
      this.cache.getItem(this.name), 
      this.config.enableCacheCompression
    );
  }

  return store ?? {};
}

DataStore.prototype._replaceCachedStore = function(newStore: object) {
  if(this.config.enableCaching) { 
    this.cache.setItem(this.name, serialize(
      newStore, 
      this.config.enableCacheCompression
    ));
  }
}

DataStore.prototype._getQueue = function() {
  return this.queue;
}

DataStore.prototype._pushToQueue = function(key: string) {
  this.queue.push(key);
}

DataStore.prototype._inQueue = function(key: string) {
  for(let i = 0; i < this.queue.length; i++) {
    if(this.queue[i] === key) { 
      return true; 
    }
  }

  return false;
}

DataStore.prototype._removeOldestValueInQueue = function() {
  //const key = this.queue.shift(); // Remove the item from the internal queue.
  //this.delete(key);               // Remove the item from local store and cache storage.


  // Switch to using a delete with exemption method 
  // so we don't delete a logged-in user's access token by mistake.
  const ACCESS_TOKEN_KEY = AuthService.getAccessTokenKey();
  //this._removeOldestValue_ExceptExemptionList_FromQueue([ACCESS_TOKEN_KEY]);
  this._removeOldestValue_ExceptExemptionMap_FromQueue({ [ACCESS_TOKEN_KEY]: 9 });
}

DataStore.prototype._removeOldestValue_ExceptExemptionList_FromQueue = function(exemptionList?: string[]) {
  // Convert array to a Set for O(1) fast lookups
  const exemptions = new Set(exemptionList ?? []); 
  let attempts = this.queue.length;

  while(attempts > 0) {
    const key = this.queue.shift(); // Take the oldest item

    if(exemptions.has(key)) {
      this._pushToQueue(key); // Rotate exempted key to the back
      attempts--;             // Decrease count to prevent infinite loops
    } else {
      this.delete(key);     // Delete the first non-exempted key
      return;               // Exit the function immediately
    }
  }
}

DataStore.prototype._removeOldestValue_ExceptExemptionMap_FromQueue = function(exemptionPriorityMap: Record<string, number>) {
  // Item with the lowest priority will be deleted first if all items in the queue are exempted.

  if(this.queue.length === 0) { 
    return; 
  }

  let attempts = this.queue.length;
  let lowestPriorityKey = null;
  let lowestPriorityValue = Infinity;
  let lowestPriorityIndex = -1;

  // Try to find the oldest non-exempted item
  while (attempts > 0) {
    const key = this.queue.shift();
    
    // Check if the key is exempted
    if(key in exemptionPriorityMap) {
      const currentPriority = exemptionPriorityMap[key];
      
      // Track the item with the lowest priority (smallest number)
      if(currentPriority < lowestPriorityValue) {
        lowestPriorityValue = currentPriority;
        lowestPriorityKey = key;
        // The index will be at the back of the queue after we push it
        lowestPriorityIndex = this.queue.length; 
      }

      this._pushToQueue(key); // Rotate to the back
      attempts--;
    } else {
      // Found a non-exempted item! Delete it and exit.
      this.delete(key);;
      return;
    }
  }

  // If we reach here, ALL items in the queue were exempted.
  // Remove the lowest priority item from its current position in the queue.
  if(lowestPriorityKey !== null) {
    this.queue.splice(lowestPriorityIndex, 1);
    this.delete(lowestPriorityKey);
  }
}

// Public API 

DataStore.prototype.get = function(key: string) {
  return this._getStore()[key];
}

DataStore.prototype.set = function(key: string, value) {
  this.store = this._getStore();
  
  this.store[key] = value;

  try {
    // Try to add the updated store, containing the new item to add, 
    // to local storage...
    this._replaceCachedStore(this.store);

    // If the update was successful, 
    // push the recently added item to the end of the queue.
    if(!this._inQueue(key)) { 
      this._pushToQueue(key); 
    }
  } catch(error) {
    if(error.name === "QuotaExceededError") { 
      logger.error("LocalStorage quota exceeded. Deleting some items to make space...");

      // If adding the item fails due to large size, 
      // remove the oldest item from the store.
      // This reduces the size of the store object.
      this._removeOldestValueInQueue(); 
    
      // Then try adding the item again. 
      // Repeate the process until a large enough item 
      // has been removed from the store to allow the new item.
      this.set(key, value);  
    } else {
      logger.error(`Error adding item ${key} to cache`, error);
    }            
  }
}

DataStore.prototype.delete = function(key: string) {
  this.store = this._getStore();

  if(this.store[key]) {
    delete this.store[key];

    try {
      this._replaceCachedStore(this.store);
    } catch(error) {
      logger.error(error);
    }
  }
}


// Helper functions 
function serialize(data, shouldCompress?: boolean): string { 
  const json = toJSON(data);
  return shouldCompress ? compress(json) : json;
}

function deserialize(serializedData: string, isCompressed?: boolean) {
  const jsonStr = isCompressed ? decompress(serializedData) : serializedData;

  return parseJSON(jsonStr);
}

/**
 * To do away with this compress function, 
 * - First, replace all calls to `compress` with `JSON.stringify`
 * - You can then delete this function if you want.
 */
function compress(data: string): string {
  return LZString.compressToUTF16(data);
}

/**
 * To do away with this decompress method, 
 * - First, replace all calls to `decompress` with `JSON.parse` 
 * - You can then delete this function if you want.
 */
function decompress(compressedString: string) {
  return LZString.decompressFromUTF16(compressedString);
}

function parseJSON(jsonString: string) {
  try {
    return JSON.parse(jsonString);
  } catch(error) {
    logger.error(error);
    return undefined;
  }
}

function toJSON(data): string {
  return JSON.stringify(data);
}